// From https://www.redblobgames.com/maps/terrain-from-noise/
// Copyright 2015 Red Blob Games <redblobgames@gmail.com>
// License: Apache v2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>

/* Render a 2D array to webgl, placing the elevation and biome data into gl attributes
 */

import { draw } from "./render-common.js";

const lightSource1 = {x: 0.2, y: 0.5, z:-0.6};
const lightSource2 = {x:-0.7, y:-0.2, z:-0.5};

export default class Renderer {
    VERT = `
        precision mediump float;
        uniform mat4 u_camera;
        attribute vec3 a_rgb;
        attribute vec3 a_xyz; // 0 to width, 0 to height
        varying vec3 v_rgb;
        void main() {
            v_rgb = a_rgb;
            gl_Position = u_camera * vec4(a_xyz, 1.0);
        }`;

    FRAG = `
        precision mediump float;
        varying vec3 v_rgb;
        void main() {
           gl_FragColor = vec4(v_rgb, 1);
        }`;

    constructor(elementId, map, colorMap) {
        this.draw = draw;
        this.elementId = elementId;
        this.map = map;
        this.colorMap = colorMap;
    }

    setup() {
        this.gl = this.getContext();
        this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        this.gl.clearColor(1.0, 1.0, 1.0, 1.0);
        this.gl.enable(this.gl.DEPTH_TEST);

        this.initShaders();
        this.initBuffers();
    }

    getContext() {
        const options = { preserveDrawingBuffer: true };
        return this.canvas.getContext('webgl', options);
    }

    initShaders() {
        const {gl} = this;

        const vs = gl.createShader(gl.VERTEX_SHADER);
        gl.shaderSource(vs, this.VERT);
        gl.compileShader(vs);

        const fs = gl.createShader(gl.FRAGMENT_SHADER);
        gl.shaderSource(fs, this.FRAG);
        gl.compileShader(fs);

        this.shader = gl.createProgram();
        gl.attachShader(this.shader, vs);
        gl.attachShader(this.shader, fs);
        gl.linkProgram(this.shader);
        gl.useProgram(this.shader);
    }

    initBuffers() {
        const {gl} = this;
        this.C = new Float32Array(this.map.w * this.map.h * 36);
        this.D = new Float32Array(this.map.w * this.map.h * 36);
        this.Cb = gl.createBuffer();
        this.Db = gl.createBuffer();

        gl.bindBuffer(gl.ARRAY_BUFFER, this.Cb);
        gl.bufferData(gl.ARRAY_BUFFER, this.C, gl.DYNAMIC_DRAW);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.Db);
        gl.bufferData(gl.ARRAY_BUFFER, this.D, gl.DYNAMIC_DRAW);

        this.c_attribute = gl.getAttribLocation(this.shader, "a_rgb");
        gl.enableVertexAttribArray(this.c_attribute);
        gl.vertexAttribPointer(this.c_attribute, 3, gl.FLOAT, false, 0, 0);

        this.d_attribute = gl.getAttribLocation(this.shader, "a_xyz");
        gl.enableVertexAttribArray(this.d_attribute);
        gl.vertexAttribPointer(this.d_attribute, 3, gl.FLOAT, false, 0, 0);

        this.camera_uniform = gl.getUniformLocation(this.shader, "u_camera");
    }

    updateBuffers() {
        const {gl, map, colorMap} = this;
        let {D, C} = this;
        let base = [-map.w / 2, -map.h / 2, 50];
        let p = 0, q = 0; // pointer into D and C arrays
        let heightscale = -map.h / 2;

        function addTriangle(v, c) {
            let x1 = v[0][0], y1 = v[0][1], z1 = v[0][2], x2 = v[1][0], y2 = v[1][1], z2 = v[1][2], x3 = v[2][0], y3 = v[2][1], z3 = v[2][2];
            D[p++] = base[0] + x1; D[p++] = base[1] + y1; D[p++] = base[2] + z1;
            D[p++] = base[0] + x2; D[p++] = base[1] + y2; D[p++] = base[2] + z2;
            D[p++] = base[0] + x3; D[p++] = base[1] + y3; D[p++] = base[2] + z3;

            // cross product, then get normal vector (but unnormalized for I am lazy)
            let ux = x2 - x1, uy = y2 - y1, uz = z2 - z1, vx = x3 - x1, vy = y3 - y1, vz = z3 - z1;
            let cx = uy * vz - uz * vy, cy = ux * vz - uz * vx, cz = ux * vy - uy * vx;
            // dot with light vector (also unnormalized for I am lazy)
            let dot1 = cx * lightSource1.x + cy * lightSource1.y + cz * lightSource1.z;
            let dot2 = cx * lightSource2.x + cy * lightSource2.y + cz * lightSource2.z;
            if (dot1 < 0.0)
                dot1 = 0.0;
            if (dot2 < 0.0)
                dot2 = 0.0;
            let light = 0.8 + 0.1 * dot1 + 0.2 * dot2;

            for (let k = 0; k < 3; k++) {
                C[q++] = light * c[k][0] / 255; C[q++] = light * c[k][1] / 255; C[q++] = light * c[k][2] / 255;
            }
        }

        function z(x, y) { return heightscale * map.get(x, y); }
        function c(x, y) { return colorMap(x, y, map.get(x, y)); }
        let V1 = [0, 0, 0], V2 = [0, 0, 0], V3 = [0, 0, 0], V = [V1, V2, V3];
        for (let x = 0; x < map.w - 2; x++) {
            for (let y = 0; y < map.h - 2; y++) {
                // Each 2x2 block will turn into 4 right triangles, with
                // x+1,y+1 shared among all of them
                let z11 = z(x + 1, y + 1), c11 = c(x + 1, y + 1),
                    z00 = z(x, y),         c00 = c(x, y),
                    z20 = z(x + 2, y),     c20 = c(x + 2, y),
                    z02 = z(x, y + 2),     c02 = c(x, y + 2),
                    z22 = z(x + 2, y + 2), c22 =c(x + 2, y + 2);

                V1[0] = x + 1; V1[1] = y + 1; V1[2] = z11;
                V2[0] = x;     V2[1] = y;     V2[2] = z00;
                V3[0] = x + 2; V3[1] = y;     V3[2] = z20;
                addTriangle(V, [c11, c00, c20]);
                V2[0] = x; V2[1] = y + 2; V2[2] = z02;
                V3[0] = x; V3[1] = y;     V3[2] = z00;
                addTriangle(V, [c11, c02, c00]);
                V2[0] = x + 2; V2[1] = y;     V2[2] = z20;
                V3[0] = x + 2; V3[1] = y + 2; V3[2] = z22;
                addTriangle(V, [c11, c20, c22]);
                V2[0] = x + 2; V2[1] = y + 2; V2[2] = z22;
                V3[0] = x;     V3[1] = y + 2; V3[2] = z02;
                addTriangle(V, [c11, c22, c02]);
            }
        }

        gl.bindBuffer(gl.ARRAY_BUFFER, this.Cb);
        gl.bufferData(gl.ARRAY_BUFFER, C, gl.DYNAMIC_DRAW);
        gl.vertexAttribPointer(this.c_attribute, 3, gl.FLOAT, false, 0, 0);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.Db);
        gl.bufferData(gl.ARRAY_BUFFER, D, gl.DYNAMIC_DRAW);
        gl.vertexAttribPointer(this.d_attribute, 3, gl.FLOAT, false, 0, 0);
    }

    _draw() {
        const {gl} = this;
        let s = 0.2 * Math.PI;
        let t = 0.4 * Math.PI;
        let a = Math.cos(t), b = Math.sin(t), c = Math.cos(s), d = Math.sin(s);
        let k = 1.7 / Math.max(this.map.w, this.map.h);
        let xScale = k, yScale = k * 2.0, zScale = k * 0.01;
        this.camera = [
            b * xScale,  a * c * yScale,  a * d * zScale,  0,
            a * xScale, -b * c * yScale, -b * d * zScale,  0,
                     0,     -d * yScale,      c * zScale,  0,
                     0,               0,               0,  1
        ];

        this.updateBuffers();
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        gl.uniformMatrix4fv(this.camera_uniform, false, this.camera);
        gl.drawArrays(gl.TRIANGLES, 0, this.C.length / 3);

        gl.flush();
    }
}
